import type { StandardServicePageContent } from "~/content/servicePage";

export const GasSafetyCheckContent: StandardServicePageContent = {
  heroTitle: "Przegląd instalacji gazowej",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Jak często",
      value: "Minimum raz w roku",
      description:
        "To obowiązkowa kontrola wynikająca z art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego. Termin warto traktować jako minimum, nie jako moment, do którego trzeba czekać przy objawach awarii.",
    },
    {
      label: "Ile kosztuje",
      value: "Zwykle 100-250 zł za mieszkanie",
      description:
        "Dom z kotłem i większą liczbą urządzeń jest zwykle wyceniany wyżej, najczęściej od około 200 zł wzwyż. Znaczenie ma liczba urządzeń, zakres kontroli i miasto realizacji.",
    },
    {
      label: "Co obejmuje",
      value: "Szczelność, zawory, urządzenia, wentylację i protokół",
      description:
        "Kontrola nie kończy się na samych przewodach. Liczy się też bezpieczna praca urządzeń, warunki wentylacji i odprowadzania spalin oraz dokument końcowy z oceną stanu instalacji.",
    },
    {
      label: "Kiedy działać wcześniej",
      value: "Przy zapachu gazu, żółtym płomieniu lub po zmianach w instalacji",
      description:
        "Dodatkową kontrolę warto zlecić po wymianie urządzenia, remoncie, problemach ze spalaniem albo wtedy, gdy nie ma pewności, kiedy wykonano ostatni przegląd.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd instalacji gazowej",
  sections: [
    {
      title: "Na czym polega przegląd instalacji gazowej i co sprawdza",
      paragraphs: [
        `Przegląd instalacji gazowej polega na sprawdzeniu, czy instalacja i podłączone do niej urządzenia mogą być dalej bezpiecznie użytkowane. Celem kontroli jest wykrycie nieszczelności, zużytych elementów armatury, błędów eksploatacyjnych oraz problemów, które mogłyby doprowadzić do wycieku gazu, pożaru, wybuchu albo emisji tlenku węgla.`,
        `To nie jest tylko formalność wykonywana do dokumentów. Dobrze przeprowadzona kontrola pozwala ocenić realny stan przewodów, połączeń, zaworów, urządzeń gazowych i warunków, w których one pracują. Dzięki temu właściciel lub zarządca wie, czy instalacja jest bezpieczna, czy wymaga naprawy, czy też potrzebna jest szersza interwencja serwisowa.`,
        `Przegląd gazowy ma znaczenie zarówno w codziennym użytkowaniu, jak i przy wynajmie, sprzedaży nieruchomości, odbiorach technicznych czy rozmowach z ubezpieczycielem. To jeden z tych obszarów, w których brak reakcji zwykle wychodzi na jaw dopiero wtedy, gdy problem staje się poważny.`,
      ],
    },
    {
      title: "Obowiązkowy przegląd instalacji gazowej co rok i kiedy zrobić go wcześniej",
      paragraphs: [
        `Przegląd instalacji gazowej jest obowiązkowy co najmniej raz w roku. Wynika to z art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego, który nakłada na właściciela lub zarządcę obowiązek okresowej kontroli instalacji gazowych oraz przewodów kominowych.`,
        `Ten obowiązek dotyczy nie tylko budynków wielorodzinnych czy komercyjnych, ale również budynków mieszkalnych jednorodzinnych wyposażonych w instalację gazową. W praktyce roczny termin warto traktować jako absolutne minimum organizacyjne.`,
        `Jeśli pojawia się zapach gazu, problemy ze spalaniem, żółty płomień, nietypowe zachowanie urządzeń albo wykonano prace przy instalacji, kontrolę należy zlecić wcześniej, bez czekania na kolejny termin ustawowy.`,
      ],
    },
    {
      title: "Przegląd instalacji gazowej w domu i mieszkaniu",
      paragraphs: [
        `W domu jednorodzinnym przegląd instalacji gazowej obejmuje zwykle nie tylko kuchenkę czy kocioł, ale cały układ pracujący w budynku: przewody, zawory, urządzenia grzewcze, przygotowanie ciepłej wody oraz warunki wentylacji i odprowadzania spalin. W wielu domach dochodzą do tego kotłownie, garaże albo pomieszczenia techniczne, które wymagają osobnej oceny.`,
        `W mieszkaniu zakres bywa mniejszy, ale nie mniej istotny. Szczególne znaczenie ma stan podłączeń do urządzeń, szczelność przewodów, dostępność zaworów, prawidłowa wentylacja pomieszczeń i bezpieczna praca urządzeń gazowych w układzie całego budynku.`,
        `Niezależnie od typu nieruchomości, przegląd ma potwierdzić, że instalacja odpowiada aktualnemu sposobowi użytkowania lokalu. To ważne zwłaszcza po remoncie, wymianie urządzeń, zmianie układu pomieszczeń albo wtedy, gdy przez długi czas nie było pewności, kto i kiedy wykonywał poprzednią kontrolę.`,
      ],
    },
    {
      title: "Protokół z przeglądu instalacji gazowej i cena usługi",
      paragraphs: [
        `Po wykonaniu przeglądu właściciel lub zarządca powinien otrzymać protokół potwierdzający zakres kontroli, datę wykonania i ocenę stanu instalacji. Taki dokument porządkuje dokumentację budynku, przydaje się przy ubezpieczeniu, wynajmie i sprzedaży oraz potwierdza wykonanie obowiązkowej kontroli.`,
        `W protokole powinny znaleźć się nie tylko dane formalne, ale też wykryte nieprawidłowości, zalecenia naprawcze i informacja, czy instalacja może być dalej bezpiecznie użytkowana. Sama obecność podpisu bez konkretnej oceny technicznej niewiele mówi o rzeczywistym stanie układu.`,
        `Cena przeglądu instalacji gazowej zależy od liczby urządzeń, rodzaju nieruchomości, wielkości instalacji i zakresu diagnostyki. W mieszkaniu koszt bywa niższy niż w domu z kotłem i podgrzewaczem, ale o wartości oferty decyduje przede wszystkim to, czy obejmuje realną kontrolę, protokół i ocenę warunków bezpiecznej pracy urządzeń.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu instalacji gazowej",
  scopeItems: [
    "Oględziny instalacji gazowej",
    "Próba szczelności",
    "Kontrola podłączeń do urządzeń",
    "Sprawdzenie zaworów odcinających",
    "Ocena stanu rur i połączeń",
    "Kontrola wentylacji pomieszczeń",
    "Weryfikacja warunków spalania",
    "Protokół pokontrolny",
  ],
  seo: {
    title: "Przegląd instalacji gazowej co rok: cena, zakres i protokół",
    description:
      "Zamów przegląd instalacji gazowej dla domu, mieszkania lub firmy. Sprawdź obowiązek corocznej kontroli, zakres badania, protokół i to, co wpływa na cenę usługi.",
  },
};
